// eslint-disable-next-line no-unused-vars
import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { requestStarted, requestFinished } from '~/stores/redux/common/loadingSlice';
import rankApi from './rank.api';
import togglePublishedState from '~/api/togglePublishedState.api';
import { alertSwal } from '~/utils/helpers';
import { handleThunkException } from '~/services/thunk.service';

export const fetchRankApi = createAsyncThunk(
    'heroSaga/rank/fetchRankApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const data = await rankApi.index();
            if (data) {
                dispatch(requestFinished());
                return data;
            }
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        }
    },
);

export const toggleRankPublishedStateApi = createAsyncThunk(
    'heroSaga/rank/toggleRankPublishedStateApi',
    async (id, { dispatch, rejectWithValue }) => {
        try {
            await togglePublishedState(id);

            return id;
        } catch (error) {
            handleThunkException(error, dispatch);

            return rejectWithValue();
        }
    },
);

export const createRankApi = createAsyncThunk(
    'heroSaga/rank/createRankApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const { data } = await rankApi.create();

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const storeRankApi = createAsyncThunk(
    'heroSaga/rank/storeRankApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, values } = params;

        try {
            dispatch(requestStarted());
            const hero = await rankApi.store(values);
            alertSwal('success', 'Rank created successfully!');

            return hero;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const showRankApi = createAsyncThunk(
    'rankSaga/rank/showRankApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { id, navigate } = params;

        try {
            dispatch(requestStarted());
            const { data } = await rankApi.show(id);

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const updateRankApi = createAsyncThunk(
    'heroSaga/rank/updateRankApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, id, values } = params;

        try {
            dispatch(requestStarted());
            await rankApi.update(id, values);
            alertSwal('success', 'Rank updated successfully!');

            return values;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const deleteRankApi = createAsyncThunk('heroSaga/rank/deleteRankApi', async (params, { dispatch }) => {
    const { navigate, id } = params;

    try {
        await rankApi.delete(id);

        return id;
    } catch (error) {
        handleThunkException(error, dispatch, navigate);
    }
});

export const rankSlice = createSlice({
    name: 'heroSaga/rank',
    initialState: { rows: [], model: {}, editedModel: {}, pictureAttributeNames: [] }, //model is initialValues that are used for formik.
    reducers: {
        clearEditedRank(state) {
            state.editedModel = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchRankApi.fulfilled, (state, { payload }) => {
                return {
                    ...state,
                    rows: payload,
                };
            })
            .addCase(fetchRankApi.rejected, (state, { payload = [] }) => {
                state.rows = payload;
            });

        builder
            .addCase(toggleRankPublishedStateApi.fulfilled, (state, { payload }) => {
                const updateRow = state.rows.find((row) => row.id === payload);
                updateRow.isReady = !updateRow.isReady;
            })
            .addCase(toggleRankPublishedStateApi.rejected, () => {
                //do nothing
                console.log('rejected::toggleRankPublishedStateApi');
            });

        builder
            .addCase(createRankApi.fulfilled, (state, { payload }) => {
                state.model = payload.model;
                state.pictureAttributeNames = payload.pictureAttributeNames;
            })
            .addCase(createRankApi.rejected, () => {
                //
            });

        builder
            .addCase(storeRankApi.fulfilled, (state) => {
                return {
                    ...state,
                    rows: [], //clear all rows so that api fetchAll will be called again in index page.
                };
            })
            .addCase(storeRankApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(showRankApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload.model;
                state.pictureAttributeNames = payload.pictureAttributeNames;
            })
            .addCase(showRankApi.rejected, () => {
                //
            });

        builder
            .addCase(updateRankApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload; //this is to make Update component re-render so that the checkDuplicateName() will have the latest `initialName`.

                if (state.rows.length === 0) return;

                const updatedRow = state.rows.find((row) => row.id === payload.id);
                updatedRow.name = payload.name;
                updatedRow.traitAmount = payload.traitAmount;
                updatedRow.statPoint = payload.statPoint;
            })
            .addCase(updateRankApi.rejected, () => {
                //
            });

        builder.addCase(deleteRankApi.fulfilled, (state, { payload }) => {
            return {
                ...state,
                rows: state.rows.filter(({ id }) => id !== payload),
            };
        });
    },
});

export const { clearEditedRank } = rankSlice.actions;
